'use client';

import type {
  AvailabilityState,
  CheckoutAdditionalFlagType,
  LineItemFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import { PriceDiscountLocalized } from '@packages/modules/src/Price/PriceDiscountLocalized/PriceDiscountLocalized';
import { useFormatDiscount } from '@packages/shared/src/hooks/useFormatDiscount/useFormatDiscount';
import { usePriceFormatter } from '@packages/shared/src/hooks/usePriceFormatter/usePriceFormatter';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Flag } from '@packages/shared/src/ui/Flag/Flag';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceUnitPrice } from '@packages/shared/src/ui/Price/PriceUnitPrice/PriceUnitPrice';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { MessageValue } from 'react-intl';
import { ServiceItems } from '../ServiceItems/ServiceItems.client';
import { EfficiencyFlags } from '../../EfficiencyFlags/Order/EfficiencyFlags.client';

const getFlagVariant = (
  flagType: CheckoutAdditionalFlagType | undefined | null,
): 'neutral' | 'secondary' | 'sustainability' | 'sale' | 'contrast' | undefined => {
  const typeVariantMap: Record<
    CheckoutAdditionalFlagType,
    'neutral' | 'secondary' | 'sustainability' | 'sale' | 'contrast'
  > = {
    CUSTOM: 'contrast',
    NEUTRAL: 'neutral',
    SALE: 'sale',
    SECONDARY: 'secondary',
    SUSTAINABILITY: 'sustainability',
  };

  return flagType ? typeVariantMap[flagType] : undefined;
};

type AddedProductProps = {
  lineItem: LineItemFragmentFragment;
  taxHintSlot?: ReactNode;
};

const availabilityStateColorClasses: Record<AvailabilityState, string> = {
  AVAILABLE: 'text-color-success',
  NOT_AVAILABLE: 'text-color-error',
  NOT_YET_AVAILABLE: 'text-color-warning',
  UNSELECTED: 'text-color-primary',
};

export const AddedProduct = ({ lineItem, taxHintSlot }: AddedProductProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormatter('ATB');
  const { formatDiscount } = useFormatDiscount();

  const { product } = lineItem;

  if (!product) {
    return null;
  }
  const {
    imageUrl,
    name,
    variation,
    powerEfficiencyFlags: powerEfficiency,
    tireEfficiencyFlags: tireEfficiency,
    marketingFlag,
    additionalFlags,
  } = product;

  const {
    id,
    price,
    quantity,
    singlePrice,
    aplus,
    availability: { message: deliveryMessage, state: availabilityState },
  } = lineItem;

  const formattedDiscount = (() => {
    if (!price.saving?.value) return undefined;
    const formatted =
      price.saving.type === 'PERCENT'
        ? formatDiscount({ discountPercentage: price.saving.value })
        : formatDiscount({ priceDifference: price.saving.value, currency: price.currency });
    return `-${formatted}`;
  })();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1.5">
        <div className="relative h-20 w-20 flex-shrink-0 md:h-[7.5rem] md:w-[7.5rem]">
          <Image src={imageUrl} className="object-contain" fill alt="" sizes="25vw" />
        </div>
        <div className="flex flex-grow flex-col gap-1">
          {marketingFlag && (
            <div>
              <Flag text={marketingFlag.text} variant="contrast" />
            </div>
          )}
          {additionalFlags && additionalFlags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {additionalFlags.map((flag) =>
                flag.src ? (
                  <div
                    key={flag.text}
                    className="relative h-3 max-h-3 w-[5.625rem] max-w-[5.625rem]"
                  >
                    <Image src={`${flag.src}.png`} alt={flag.text} fill />
                  </div>
                ) : (
                  <Flag key={flag.text} text={flag.text} variant={getFlagVariant(flag.type)} />
                ),
              )}
            </div>
          )}
          <h2 className="text-size-headline-sm md:text-size-headline-base font-semibold md:font-normal">
            {name}
          </h2>
          <span className="text-size-body-sm">{variation}</span>
          <div className="flex flex-col">
            <span className="text-size-body-sm">
              {singlePrice &&
                formatMessage<{
                  readonly quantity: MessageValue;
                  readonly singlePrice: MessageValue;
                }>(
                  {
                    id: 'atblayer.singleprice.label',
                    defaultMessage: 'Anzahl {quantity} (á {singlePrice})',
                  },
                  {
                    quantity,
                    singlePrice: formatPrice(singlePrice.amount, singlePrice.currency),
                  },
                )}
            </span>
            <span
              className={`text-size-body-sm ${availabilityStateColorClasses[availabilityState]}`}
            >
              {deliveryMessage}
            </span>
          </div>
          <div className="mt-1.5 flex flex-col gap-1 md:flex-row">
            <div className="flex-grow">
              {((powerEfficiency && powerEfficiency.length > 0) ||
                (tireEfficiency && tireEfficiency.length > 0)) && (
                <EfficiencyFlags
                  powerFlags={powerEfficiency}
                  tireFlags={tireEfficiency}
                  tireDataSheet={tireEfficiency?.[0]?.dataSheet}
                />
              )}
            </div>
            <div className="flex flex-col">
              <Price className="self-stretch whitespace-nowrap" alignment="right" size="small">
                <>
                  {price.valueOld && (
                    <PriceDiscountLocalized
                      oldPrice={`${price.uvp ? 'UVP:' : 'statt'} ${formatPrice(price.valueOld, price.currency)}`}
                      oldPriceType={price.uvp ? 'msrp' : undefined}
                      discount={formattedDiscount}
                    />
                  )}

                  {price.value != null && (
                    <PriceValue>
                      {price.isStartPrice && <span className="text-size-body-sm mr-1">ab </span>}
                      <span>{formatPrice(price.value, price.currency)}</span>
                    </PriceValue>
                  )}

                  {price.unit && price.valueUnit && (
                    <PriceUnitPrice>
                      {`${formatPrice(price.valueUnit, price.currency)} / ${price.unit}`}
                    </PriceUnitPrice>
                  )}
                </>
              </Price>
              {taxHintSlot}
            </div>
          </div>
        </div>
      </div>
      {aplus && id && (
        <>
          <Divider />
          <ServiceItems aplusData={aplus} pli={id} />
        </>
      )}
    </div>
  );
};
